import { useState } from "react";
import { useModalStore } from "../../../stores/modalStore.js";

const BOT_COMMANDS = [
  { category: 'Moderation', commands: [
    { name: '/ban',    args: '<user> [reason]',  desc: 'Ban a user from the server' },
    { name: '/kick',   args: '<user> [reason]',  desc: 'Kick a user from the server' },
    { name: '/mute',   args: '<user> <duration>',desc: 'Timeout a user (e.g., 10m, 1h)' },
    { name: '/warn',   args: '<user> <reason>',  desc: 'Issue a formal warning' },
    { name: '/clear',  args: '<1-100>',           desc: 'Bulk delete messages in channel' },
    { name: '/slowmode', args: '<seconds>',      desc: 'Set channel slow mode delay' },
  ]},
  { category: 'Utility', commands: [
    { name: '/poll',   args: '"question" opt1 opt2 …', desc: 'Create a poll with up to 10 options' },
    { name: '/remind', args: '<time> <message>',  desc: 'Set a reminder for yourself' },
    { name: '/roll',   args: '[NdM]',             desc: 'Roll dice (default: 1d6)' },
    { name: '/8ball',  args: '<question>',        desc: 'Ask the magic 8-ball' },
    { name: '/coin',   args: '',                  desc: 'Flip a coin' },
    { name: '/avatar', args: '[user]',            desc: 'Show a user\'s avatar' },
    { name: '/server', args: '',                  desc: 'Show server stats' },
    { name: '/user',   args: '[user]',            desc: 'Show user info & join date' },
  ]},
  { category: 'Fun', commands: [
    { name: '/meme',   args: '',                  desc: 'Post a random meme' },
    { name: '/cat',    args: '',                  desc: 'Post a random cat picture' },
    { name: '/dog',    args: '',                  desc: 'Post a random dog picture' },
    { name: '/quote',  args: '',                  desc: 'Post an inspirational quote' },
    { name: '/trivia', args: '[category]',        desc: 'Start a trivia question' },
  ]},
  { category: 'Music', commands: [
    { name: '/play',   args: '<song/URL>',        desc: 'Play music in a voice channel' },
    { name: '/skip',   args: '',                  desc: 'Skip the current track' },
    { name: '/queue',  args: '',                  desc: 'Show the music queue' },
    { name: '/stop',   args: '',                  desc: 'Stop music and clear queue' },
    { name: '/volume', args: '<0-100>',           desc: 'Set music volume' },
  ]},
];

export function BotCommandsModal() {
  const { isOpen, type, onClose } = useModalStore();
  const [search,  setSearch]  = useState('');
  const [selCat,  setSelCat]  = useState('all');
  const [copied,  setCopied]  = useState(null);

  if (!isOpen || type !== 'botCommands') return null;

  const allCategories = BOT_COMMANDS.map(c => c.category);

  const filtered = BOT_COMMANDS
    .filter(c => selCat === 'all' || c.category === selCat)
    .map(c => ({
      ...c,
      commands: c.commands.filter(cmd =>
        !search || cmd.name.includes(search.toLowerCase()) || cmd.desc.toLowerCase().includes(search.toLowerCase())
      ),
    }))
    .filter(c => c.commands.length > 0);

  const copy = (text) => { navigator.clipboard?.writeText(text); setCopied(text); setTimeout(() => setCopied(null), 1500); };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={onClose}>
      <div style={{ width: '100%', maxWidth: 640, maxHeight: '88vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary,#13141f)', borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,0.6)', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>🤖 Bot Commands</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>All available slash commands</div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 18, cursor: 'pointer' }}>✕</button>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search commands…"
              style={{ flex: 1, padding: '7px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, outline: 'none' }} />
          </div>
        </div>

        {/* Category tabs */}
        <div style={{ display: 'flex', gap: 4, padding: '8px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', overflowX: 'auto', flexShrink: 0 }}>
          {['all', ...allCategories].map(c => (
            <button key={c} onClick={() => setSelCat(c)}
              style={{ padding: '4px 12px', borderRadius: 20, border: '1px solid', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: 12, transition: 'all 0.15s',
                background: selCat === c ? 'rgba(99,102,241,0.25)' : 'rgba(255,255,255,0.05)',
                borderColor: selCat === c ? 'rgba(99,102,241,0.5)' : 'rgba(255,255,255,0.1)',
                color: selCat === c ? '#a5b4fc' : 'rgba(255,255,255,0.5)',
              }}>
              {c === 'all' ? 'All' : c}
            </button>
          ))}
        </div>

        {/* Command list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>No commands match "{search}"</div>
          ) : (
            filtered.map(group => (
              <div key={group.category} style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, padding: '0 4px' }}>
                  {group.category}
                </div>
                {group.commands.map(cmd => (
                  <div key={cmd.name}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, marginBottom: 4, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer' }}
                    onClick={() => copy(cmd.name + (cmd.args ? ` ${cmd.args}` : ''))}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.08)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#a5b4fc', fontFamily: 'monospace' }}>{cmd.name}</span>
                        {cmd.args && <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>{cmd.args}</span>}
                      </div>
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>{cmd.desc}</div>
                    </div>
                    <span style={{ fontSize: 11, color: copied === (cmd.name + (cmd.args ? ` ${cmd.args}` : '')) ? '#22c55e' : 'rgba(255,255,255,0.2)', flexShrink: 0 }}>
                      {copied === (cmd.name + (cmd.args ? ` ${cmd.args}` : '')) ? '✓ Copied' : 'Copy'}
                    </span>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
